import { useEffect, useRef, useState } from 'react';
import Modal from 'react-modal';
import AvatarEditor from 'react-avatar-editor';
import { UserDetailDto } from '@ivy-monorepo/shared';
import ImageDropzone from '../ui/ImageDropzone';
import { adminService } from 'src/services/admin.service';
import { BaseModalProps } from 'src/types/modal';

interface UserPhotoModalProps extends BaseModalProps {
  userPublicId: string
  /** Whether the user currently has a photo — shows the "Remove Photo" action. */
  hasPhoto: boolean
  onSaved: (user: UserDetailDto) => void
}

const IMAGE_SIZE = 400;
const MIN_ZOOM_SIZE = 1;
const MAX_ZOOM_SIZE = 200;

const UserPhotoModal = ({ isOpen, onClose, userPublicId, hasPhoto, onSaved }: UserPhotoModalProps) => {
  const editorRef = useRef<AvatarEditor>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [zoom, setZoom] = useState(100)
  const [rotation, setRotation] = useState(0)
  const [imageFile, setImageFile] = useState<File | null>(null)

  useEffect(() => {
    if (!isOpen) {
      setImageFile(null)
      setZoom(100)
      setRotation(0)
      setIsUploading(false)
      setError(null)
    }
  }, [isOpen])

  const fileUploadHandle = (file: File) => {
    setError(null)
    setImageFile(file)
  }

  const changeFileHandle = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/png,image/jpeg,image/webp'

    input.onchange = (e: Event) => {
      const target = e.target as HTMLInputElement
      const file = target.files?.[0]
      if (file) {
        setError(null)
        setImageFile(file)
      }
    }

    input.click()
  }

  const submitHandle = () => {
    if (!editorRef.current) return

    const canvas = editorRef.current.getImageScaledToCanvas()
    setIsUploading(true)
    setError(null)

    canvas.toBlob(async (blob) => {
      if (!blob) {
        setIsUploading(false)
        return
      }

      const file = new File([blob], 'photo.png', { type: blob.type || 'image/png' })

      try {
        const updated = await adminService.updateUserPhoto(userPublicId, file)
        onSaved(updated)
        onClose()
      } catch (e: any) {
        const message = e?.response?.data?.userMessage || e?.response?.data?.message || 'Failed to update user photo'
        setError(typeof message === 'string' ? message : 'Failed to update user photo')
      } finally {
        setIsUploading(false)
      }
    })
  }

  const removeHandle = async () => {
    const confirmed = window.confirm(
      "Remove this user's photo?\n\nThe image is deleted and the verified badge is cleared. The user can upload a new photo from the app.",
    )
    if (!confirmed) return

    setIsUploading(true)
    setError(null)
    try {
      const updated = await adminService.removeUserPhoto(userPublicId)
      onSaved(updated)
      onClose()
    } catch (e: any) {
      const message = e?.response?.data?.userMessage || e?.response?.data?.message || 'Failed to remove user photo'
      setError(typeof message === 'string' ? message : 'Failed to remove user photo')
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      className="modal-popup"
      overlayClassName="modal-overlay"
      onRequestClose={onClose}
    >
      <div className="user-photo-popup-content modal-content-wrapper">
        <h2>Edit Photo</h2>
        <p className="add-venue-hint">Uploading a new photo immediately marks it as verified.</p>

        {isUploading && (
          <div className="loader-wrapper">
            <div className="loader"></div>
          </div>
        )}

        <div className="logo-content-wrapper modal-body-content">
          {!imageFile && (
            <ImageDropzone
              onFileAccepted={fileUploadHandle}
              maxMbSize={8}
            />
          )}

          {imageFile && (
            <div className="editor-wrapper">
              <div className="img-outter-wrapper">
                <div className="img-wrapper" style={{ borderRadius: '50%' }}>
                  <AvatarEditor
                    ref={editorRef}
                    image={imageFile}
                    width={IMAGE_SIZE}
                    height={IMAGE_SIZE}
                    scale={zoom / 100}
                    rotate={rotation}
                    borderRadius={IMAGE_SIZE}
                    disableBoundaryChecks
                  />
                </div>
              </div>

              <div className="editor-tools">
                <div className="form-item tool-group">
                  <label>Rotate</label>
                  <input
                    type="range"
                    min={0}
                    max={360}
                    step={1}
                    value={rotation}
                    onChange={(e) => setRotation(parseFloat(e.target.value))}
                  />
                </div>

                <div className="form-item tool-group">
                  <label>Zoom</label>
                  <div className='input-row'>
                    <input
                      type='number'
                      min={MIN_ZOOM_SIZE}
                      max={MAX_ZOOM_SIZE}
                      value={zoom}
                      onChange={(e) => setZoom(Math.min(MAX_ZOOM_SIZE, parseFloat(e.target.value)))}
                    />
                    <input
                      type="range"
                      min={MIN_ZOOM_SIZE}
                      max={MAX_ZOOM_SIZE}
                      step={1}
                      value={zoom}
                      onChange={(e) => setZoom(parseFloat(e.target.value))}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {error && <div className="page-error">{error}</div>}

        <div className="modal-footer-content">
          {hasPhoto && (
            <button
              type="button"
              className="styled-button danger remove-photo-button"
              onClick={removeHandle}
              disabled={isUploading}
            >
              Remove Photo
            </button>
          )}
          <button
            className="styled-button"
            onClick={changeFileHandle}
            disabled={isUploading || !imageFile}
          >
            Change File
          </button>
          <button
            className="styled-button"
            onClick={submitHandle}
            disabled={isUploading || !imageFile}
          >
            {isUploading ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default UserPhotoModal;
